// CadastreInfoComponent.js
import { useState, useEffect, useRef } from "react";
import { EditableInputComponent, SectionHeader } from "components";
import { Document } from "assets/icons";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { FileLabel } from "shared/types/file";
import ModalUploadFileLog from "shared/components/uploadFileModal/uploadFileModal";
import { BuildingDTO } from "shared/types/building";
import { UnitDTO } from "shared/types/unit";
import { BuildingRegulatoryInfoDTO } from "shared/types/regulatory-info";
import FileLogTable from "shared/components/uploadFileModal/fileLogTable";

interface CadastreInfoComponentProps {
  buildingRegulatoryInfo: BuildingRegulatoryInfoDTO;
  handleCadastreInfoChangeInParent?: (value: any) => any;
  handleSaveInParent?: () => any;
  handleCloseParent?: () => any;
  building: BuildingDTO;
  unit: UnitDTO;
  parentEditMode?: boolean;
}

const CadastreInfoComponent = ({
  buildingRegulatoryInfo,
  handleCloseParent,
  handleSaveInParent,
  building,
  unit,
  handleCadastreInfoChangeInParent,
  parentEditMode = true,
}: CadastreInfoComponentProps) => {
  const [editMode, setEditMode] = useState(parentEditMode);
  const [editedCadastreInfo, setEditedCadastreInfo] = useState(
    buildingRegulatoryInfo,
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { session } = useSession();
  const [fileModalOpen, setFileModalOpen] = useState<boolean>(false);
  const [selectedFileLabel, setSelectedFileLabel] = useState<FileLabel | null>(
    null,
  );
  const fileLogTableRef = useRef(null);

  // Define arrays of file labels for each category
  const cadastreFileLabels = [
    FileLabel.KadastraalUittreksel,
    FileLabel.PlanKadaster,
    FileLabel.AanslagbiljetOV,
    FileLabel.KadastraalInkomen
  ];

  useEffect(() => {
    console.log(buildingRegulatoryInfo);
    setEditedCadastreInfo(buildingRegulatoryInfo);
  }, []); // don't use the dependency here because it will refresh the component on tab switch

  const handleSave = async () => {
    // Save any changes in the FileLogTable first
    if (fileLogTableRef.current && (fileLogTableRef.current as any).hasUnsavedChanges) {
      await (fileLogTableRef.current as any).saveChanges();
    }
    
    setHasUnsavedChanges(false);
    try {
      if (handleSaveInParent) {
        handleSaveInParent();
      } else {
        await fetchWithAuth(
          `${API_URL}/building/cadastre-info`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(editedCadastreInfo),
          },
          session,
        );
        toast.success("Kadastrale informatie succesvol bijgewerkt!");
      }
    } catch (e) {
      console.error(e);
      toast.error(
        "Er is een fout opgetreden bij het bijwerken van de kadastrale informatie.",
      );
    }
  };

  const handleUploadClick = (fileLabel: FileLabel) => {
    setSelectedFileLabel(fileLabel);
    setFileModalOpen(true);
  };

  const handleFileUploadSuccess = async (fileLogData: any) => {
    const { fileLog } = fileLogData;

    let updatedField = {};

    switch (selectedFileLabel) {
      case FileLabel.KadastraalUittreksel:
        updatedField = {
          cadastralExtractFileLogId: fileLog.id,
          cadastralExtractFileLog: fileLog,
        };
        break;
      case FileLabel.PlanKadaster:
        updatedField = {
          cadastralPlanFileLogId: fileLog.id,
          cadastralPlanFileLog: fileLog,
        };
        break;
      case FileLabel.AanslagbiljetOV:
        updatedField = {
          latestPropertyTaxFileLogId: fileLog.id,
          latestPropertyTaxFileLog: fileLog,
        };
        break;
      case FileLabel.KadastraalInkomen:
        updatedField = {
          cadastreIncomeFileLogId: fileLog.id,
          cadastreIncomeFileLog: fileLog,
        };
        break;
      default:
        return;
    }

    const updatedCadastreInfo = { ...buildingRegulatoryInfo, ...updatedField };

    // Save the updated information to the backend
    try {
      const response = await fetchWithAuth(
        `${API_URL}/building/cadastre-info`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatedCadastreInfo),
        },
        session,
      );

      if (response.ok) {
        toast.success("Kadastrale informatie succesvol geüpdatet");
        setHasUnsavedChanges(false); // Reset unsaved changes state
        setEditedCadastreInfo(updatedCadastreInfo); // Update local state
      } else {
        throw new Error("Failed to update Cadastre information");
      }
    } catch (error) {
      console.error("Error updating Cadastre information:", error);
      toast.error("Failed to update Cadastre information");
    }
  };

  const getInputFieldStatusColor = (value: any) => {
    return value ? "bg-green-500" : "bg-red-500"; // Green for filled, Red for empty
  };

  const updateCadastreInfo = (updatedFields: any) => {
    setHasUnsavedChanges(true);
    setEditedCadastreInfo((prev: any) => ({ ...prev, ...updatedFields }));

    if (handleCadastreInfoChangeInParent) {
      handleCadastreInfoChangeInParent({
        ...buildingRegulatoryInfo,
        ...updatedFields,
      });
    }
  };

  return (
    <section className="flex flex-col gap-0 rounded-md bg-white">
      <SectionHeader
        title="Kadastrale informatie"
        icon={<Document />}
        editMode={editMode}
        sticky={false}
        onEditClick={() => setEditMode(!editMode)}
        onSaveClick={handleSave}
        showEditToggle={true}
        hasUnsavedChanges={hasUnsavedChanges}
        onCloseClick={() => {
          if (handleCloseParent) handleCloseParent();
        }}
        hideCloseAction={true}
      />
      <div className="p-3">
        <div className="flex flex-col gap-4">
          {/* Cadastral Income */}
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getInputFieldStatusColor(
                  editedCadastreInfo?.cadastralIncome,
                )}`}
              />

              <p>Niet geïndexeerd kadastraal inkomen</p>
            </header>
            <EditableInputComponent
              label="Kadastraal inkomen"
              value={(editedCadastreInfo?.cadastralIncome as any) || ""}
              editMode={editMode}
              isLoading={false}
              onChange={(value) =>
                updateCadastreInfo({ cadastralIncome: value })
              }
            />
          </div>

          {/* Cadastre Documents Table */}
          <div className="mt-4">
            <h3 className="mb-2 text-base font-medium">Kadastrale documenten</h3>
            <FileLogTable
              ref={fileLogTableRef}
              acceptedLabels={cadastreFileLabels}
              onUploadClick={() => handleUploadClick(selectedFileLabel || FileLabel.KadastraalUittreksel)}
              building={building}
              unit={unit}
            />
          </div>

          {/* Modal for File Upload - this still gets used when uploading new files */}
          <ModalUploadFileLog
            isOpen={fileModalOpen}
            onClose={() => setFileModalOpen(false)}
            onSave={handleFileUploadSuccess}
            defaultFileLabel={selectedFileLabel as FileLabel}
            building={building}
            unit={unit}
            customLabels={[selectedFileLabel]}
          />
        </div>
      </div>
    </section>
  );
};

export default CadastreInfoComponent;
